import React from 'react';
import { Smartphone, CheckCircle2, XCircle, AlertTriangle, Clock, Loader2 } from 'lucide-react';

const NEXT_STEPS_COPY = {
  BANK_TRANSFER: 'Complete the transfer using the bank details provided, then tap "Check payment status" — we confirm automatically once it clears.',
  CREDIT_CARD: 'Your card payment is being confirmed with our payment processor. This usually takes a few seconds.',
  PAYPAL: 'Complete the payment in the PayPal window. If it didn\'t open, check your pop-up blocker.',
  DIGITAL_WALLET: 'Approve the payment request in your wallet app, then tap "Check payment status".',
};

/**
 * Payment confirmation state machine — mirrors every status the polling loop
 * in the parent container can produce: pending, pin_pending,
 * insufficient_balance, success, failed, timeout.
 */
const PaymentStatusModal = ({
  paymentStatus,
  onCheckAgain,
  onCancel,
  onGoToConfirmation,
  onRetry,
  onCloseFailed,
  onUseDifferentMethod,
  onBackToPayment,
}) => {
  if (!paymentStatus) return null;
  const { status, paymentMethod, phoneNumber, amount, orderId, checkoutRequestID, failureReason, message } = paymentStatus;

  return (
    <div className="fixed inset-0 z-[80] flex items-center justify-center p-4 bg-ink/60 backdrop-blur-sm">
      <div className="bg-surface w-full max-w-md rounded-md shadow-2xl p-8 text-center">
        {(status === 'pending') && (
          <>
            <Loader2 className="w-10 h-10 text-accent mx-auto mb-5 animate-spin" />
            {paymentMethod === 'MPESA' ? (
              <>
                <h3 className="font-display text-2xl text-ink mb-2">Check your phone</h3>
                <p className="text-sm text-ink-soft mb-1">STK push sent to {phoneNumber}</p>
                <p className="text-sm text-ink-soft mb-5">Enter your M-Pesa PIN to pay KSh {Number(amount || 0).toLocaleString()}.</p>
              </>
            ) : (
              <>
                <h3 className="font-display text-2xl text-ink mb-2">Processing payment</h3>
                <p className="text-sm text-ink-soft mb-5">{message || 'Your payment is being processed.'}</p>
              </>
            )}
            <div className="bg-paper rounded-md p-3 text-xs text-ink-faint mb-5 space-y-1">
              {orderId && <p>Order ID: {orderId}</p>}
              {checkoutRequestID && <p>Reference: {checkoutRequestID}</p>}
            </div>
            {NEXT_STEPS_COPY[paymentMethod] && (
              <div className="bg-accent-soft rounded-md p-3 text-xs text-ink mb-5 text-left">{NEXT_STEPS_COPY[paymentMethod]}</div>
            )}
            <div className="flex flex-col gap-2">
              <button onClick={onCheckAgain} className="bg-ink text-paper dpc-label py-3 rounded-full hover:bg-accent transition-colors">
                Check payment status
              </button>
              <button onClick={onCancel} className="text-sm text-ink-faint hover:text-ink">Cancel &amp; close</button>
            </div>
          </>
        )}

        {status === 'pin_pending' && (
          <>
            <Smartphone className="w-10 h-10 text-accent mx-auto mb-5" />
            <h3 className="font-display text-2xl text-ink mb-2">Authorize on your phone</h3>
            <p className="text-sm text-ink-soft mb-6">Enter your M-Pesa PIN when prompted to complete payment.</p>
            <button onClick={onCheckAgain} className="w-full bg-ink text-paper dpc-label py-3 rounded-full hover:bg-accent transition-colors">
              Check payment status
            </button>
          </>
        )}

        {status === 'insufficient_balance' && (
          <>
            <AlertTriangle className="w-10 h-10 text-brass-deep mx-auto mb-5" />
            <h3 className="font-display text-2xl text-ink mb-2">Insufficient M-Pesa balance</h3>
            <p className="text-sm text-ink-soft mb-6">Top up your wallet, then check again.</p>
            <div className="flex flex-col gap-2">
              <button onClick={onCheckAgain} className="bg-ink text-paper dpc-label py-3 rounded-full hover:bg-accent transition-colors">
                Check again
              </button>
              <button onClick={onUseDifferentMethod} className="text-sm text-ink-faint hover:text-ink">Use a different payment method</button>
            </div>
          </>
        )}

        {status === 'success' && (
          <>
            <CheckCircle2 className="w-10 h-10 text-accent mx-auto mb-5" />
            <h3 className="font-display text-2xl text-ink mb-2">Payment successful</h3>
            <p className="text-sm text-ink-soft mb-6">Your order has been confirmed.</p>
            <button onClick={onGoToConfirmation} className="w-full bg-ink text-paper dpc-label py-3 rounded-full hover:bg-accent transition-colors">
              View order confirmation
            </button>
          </>
        )}

        {status === 'failed' && (
          <>
            <XCircle className="w-10 h-10 text-accent mx-auto mb-5" />
            <h3 className="font-display text-2xl text-ink mb-2">Payment failed</h3>
            <p className="text-sm text-ink-soft mb-6">{failureReason || 'Something went wrong with your payment.'}</p>
            <div className="flex flex-col gap-2">
              <button onClick={onRetry} className="bg-ink text-paper dpc-label py-3 rounded-full hover:bg-accent transition-colors">
                Retry payment
              </button>
              <button onClick={onCloseFailed} className="text-sm text-ink-faint hover:text-ink">Close</button>
            </div>
          </>
        )}

        {status === 'timeout' && (
          <>
            <Clock className="w-10 h-10 text-brass-deep mx-auto mb-5" />
            <h3 className="font-display text-2xl text-ink mb-2">Still waiting</h3>
            <p className="text-sm text-ink-soft mb-6">We haven't received confirmation yet. You can check again or choose a different method.</p>
            <div className="flex flex-col gap-2">
              <button onClick={onCheckAgain} className="bg-ink text-paper dpc-label py-3 rounded-full hover:bg-accent transition-colors">
                Check payment status
              </button>
              <button onClick={onBackToPayment} className="text-sm text-ink-faint hover:text-ink">Back to payment methods</button>
            </div>
          </>
        )}
      </div>
    </div>
  );
};

export default PaymentStatusModal;
